import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Brain, Calculator, TrendingUp, Settings, Zap, Target, DollarSign, Info, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Slider as UISlider } from "@/components/ui/slider";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useI18n } from "@/i18n/I18nProvider";
import { formatCurrency } from "@/lib/currency";

const priceRuleSchema = z.object({
  service_id: z.string().min(1, "Service is required"),
  base_price: z.number().min(0.01, "Base price must be greater than 0"),
  area_multiplier: z.number().min(0.1, "Area multiplier must be at least 0.1"),
  urgency_multiplier: z.number().min(1, "Urgency multiplier must be at least 1"),
  complexity_multiplier: z.number().min(0.5, "Complexity multiplier must be at least 0.5"),
  max_area: z.number().min(1, "Max area must be at least 1"),
  auto_quote_enabled: z.boolean().default(false),
  min_price: z.number().min(0.01, "Minimum price must be greater than 0"),
  max_price: z.number().min(0.01, "Maximum price must be greater than 0"),
});

type PriceRuleForm = z.infer<typeof priceRuleSchema>;

interface PriceEstimate {
  minPrice: number;
  maxPrice: number;
  suggestedPrice: number;
  confidence: number;
  factors: string[];
}

interface SmartPriceEstimatorProps {
  services: any[];
  onSaveRule: (rule: PriceRuleForm) => void;
  onTestEstimate: (estimate: PriceEstimate) => void;
}

// Real-time insights component based on historical data
function RealTimeInsights({ companyId }: { companyId: string | null }) {
  const [insights, setInsights] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInsights = async () => {
      if (!companyId) {
        setLoading(false);
        return;
      }

      try {
        // Get company's historical pricing data
        const { data: pricingData, error: pricingError } = await supabase
          .from('pricing_audit')
          .select('*')
          .eq('company_id', companyId)
          .order('created_at', { ascending: false })
          .limit(100);

        if (pricingError) throw pricingError;

        // Get company's booking data for comparison
        const { data: bookingData, error: bookingError } = await supabase
          .from('bookings')
          .select('total_amount, status, created_at')
          .eq('company_id', companyId)
          .eq('status', 'completed')
          .order('created_at', { ascending: false })
          .limit(50);

        if (bookingError) throw bookingError;

        // Calculate insights
        const avgPricingEstimate = pricingData?.length > 0 
          ? pricingData.reduce((sum, p) => sum + parseFloat((p.result as any)?.suggestedPrice || 0), 0) / pricingData.length
          : 0;

        const avgActualPrice = bookingData?.length > 0
          ? bookingData.reduce((sum, b) => sum + parseFloat(b.total_amount), 0) / bookingData.length
          : 0;

        const conversionRate = pricingData?.length > 0 && bookingData?.length > 0
          ? (bookingData.length / pricingData.length) * 100
          : 0;

        const priceAccuracy = avgPricingEstimate > 0 && avgActualPrice > 0
          ? Math.abs(avgPricingEstimate - avgActualPrice) / avgActualPrice * 100
          : 0;

        setInsights({
          avgPricingEstimate,
          avgActualPrice,
          conversionRate,
          priceAccuracy,
          totalEstimates: pricingData?.length || 0,
          totalBookings: bookingData?.length || 0,
          recentTrend: pricingData?.slice(0, 10) || []
        });

      } catch (error) {
        console.error('Error fetching insights:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInsights();
  }, [companyId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="ml-2">Loading insights...</span>
      </div>
    );
  }

  if (!insights || insights.totalEstimates === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Brain className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No Data Available</h3>
          <p className="text-muted-foreground">
            Generate some price estimates to see AI insights and recommendations.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Pricing Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span>Average Estimate</span>
              <span className="font-semibold">{formatCurrency(insights.avgPricingEstimate)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Average Actual Price</span>
              <span className="font-semibold text-green-600">{formatCurrency(insights.avgActualPrice)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Price Accuracy</span>
              <Badge variant={insights.priceAccuracy < 10 ? "default" : insights.priceAccuracy < 20 ? "secondary" : "destructive"}>
                {insights.priceAccuracy.toFixed(1)}% variance
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span>Conversion Rate</span>
              <Badge variant={insights.conversionRate > 20 ? "default" : insights.conversionRate > 10 ? "secondary" : "destructive"}>
                {insights.conversionRate.toFixed(1)}%
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            AI Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {insights.priceAccuracy > 15 && (
              <div className="flex items-start gap-2">
                <Zap className="h-4 w-4 text-yellow-500 mt-0.5" />
                <div>
                  <p className="font-medium">Improve Price Accuracy</p>
                  <p className="text-sm text-muted-foreground">
                    Your estimates are {insights.priceAccuracy.toFixed(1)}% off from actual prices. Consider adjusting your multipliers.
                  </p>
                </div>
              </div>
            )}
            
            {insights.conversionRate < 15 && (
              <div className="flex items-start gap-2">
                <Zap className="h-4 w-4 text-red-500 mt-0.5" />
                <div>
                  <p className="font-medium">Low Conversion Rate</p>
                  <p className="text-sm text-muted-foreground">
                    Only {insights.conversionRate.toFixed(1)}% of estimates convert to bookings. Consider lowering your base prices.
                  </p>
                </div>
              </div>
            )}

            {insights.avgPricingEstimate > insights.avgActualPrice * 1.1 && (
              <div className="flex items-start gap-2">
                <Zap className="h-4 w-4 text-orange-500 mt-0.5" />
                <div>
                  <p className="font-medium">Pricing Above Market</p>
                  <p className="text-sm text-muted-foreground">
                    Your estimates are 10%+ higher than actual prices. Consider competitive pricing.
                  </p>
                </div>
              </div>
            )}

            {insights.priceAccuracy < 10 && insights.conversionRate > 20 && (
              <div className="flex items-start gap-2">
                <Zap className="h-4 w-4 text-green-500 mt-0.5" />
                <div>
                  <p className="font-medium">Excellent Performance</p>
                  <p className="text-sm text-muted-foreground">
                    Great job! Your pricing is accurate and converting well.
                  </p>
                </div>
              </div>
            )}

            {insights.totalEstimates < 10 && (
              <div className="flex items-start gap-2">
                <Zap className="h-4 w-4 text-blue-500 mt-0.5" />
                <div>
                  <p className="font-medium">Need More Data</p>
                  <p className="text-sm text-muted-foreground">
                    Generate more estimates to improve AI recommendations.
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function SmartPriceEstimator({ services, onSaveRule, onTestEstimate }: SmartPriceEstimatorProps) {
  const { t } = useI18n();
  const [currentEstimate, setCurrentEstimate] = useState<PriceEstimate | null>(null);
  const [testData, setTestData] = useState({
    area: 100,
    urgency: "normal",
    complexity: "medium",
    location: "urban",
  });
  const { toast } = useToast();

  // Get current company ID
  const getCurrentCompanyId = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return null;
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('companies(id)')
        .eq('user_id', session.user.id)
        .single();
      
      return (profile as any)?.companies?.[0]?.id || null;
    } catch (error) {
      console.error('Error getting company ID:', error);
      return null;
    }
  };

  const form = useForm<PriceRuleForm>({
    resolver: zodResolver(priceRuleSchema),
    defaultValues: {
      service_id: "",
      base_price: 50,
      area_multiplier: 1.5,
      urgency_multiplier: 1.2,
      complexity_multiplier: 1.0,
      max_area: 500,
      auto_quote_enabled: false,
      min_price: 30,
      max_price: 500,
    },
  });

  const calculateEstimate = (rule: PriceRuleForm, data: any): PriceEstimate => {
    const basePrice = rule.base_price;
    const areaFactor = Math.min(data.area / 100, rule.max_area / 100) * rule.area_multiplier;
    
    const urgencyMultipliers = {
      low: 0.8,
      normal: 1.0,
      high: rule.urgency_multiplier,
      urgent: rule.urgency_multiplier * 1.5,
    };
    
    const complexityMultipliers = {
      simple: 0.8,
      medium: 1.0,
      complex: rule.complexity_multiplier,
      very_complex: rule.complexity_multiplier * 1.3,
    };
    
    const locationMultipliers = {
      rural: 0.9,
      suburban: 1.0,
      urban: 1.2,
      downtown: 1.4,
    };

    const calculatedPrice = basePrice * areaFactor * 
      urgencyMultipliers[data.urgency] * 
      complexityMultipliers[data.complexity] * 
      locationMultipliers[data.location];

    const minPrice = Math.max(rule.min_price, calculatedPrice * 0.8);
    const maxPrice = Math.min(rule.max_price, calculatedPrice * 1.2);
    const suggestedPrice = Math.round(calculatedPrice);

    const factors = [];
    if (data.area > 200) factors.push("Large area");
    if (data.urgency === "urgent") factors.push("Urgent service");
    if (data.complexity === "very_complex") factors.push("High complexity");
    if (data.location === "downtown") factors.push("Premium location");

    return {
      minPrice,
      maxPrice,
      suggestedPrice,
      confidence: factors.length > 0 ? 85 : 95,
      factors,
    };
  };

  const handleTestEstimate = async () => {
    const rule = form.getValues();
    if (!rule.service_id) {
      toast({
        title: "Error",
        description: "Please select a service first",
        variant: "destructive",
      });
      return;
    }

    // Resolve company_id for logged-in vendor
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast({ title: 'Not signed in', description: 'Please sign in to test pricing', variant: 'destructive' });
      return;
    }
    const { data: profile } = await supabase
      .from('profiles')
      .select('companies(id)')
      .eq('user_id', session.user.id)
      .single();
    const companyId = (profile as any)?.companies?.[0]?.id;
    if (!companyId) {
      toast({ title: 'No company found', description: 'Complete onboarding first', variant: 'destructive' });
      return;
    }

    // Call server-side estimator to log pricing_audit
    const { data, error } = await supabase.rpc('pricing_estimate', {
      p_company_id: companyId,
      p_service_id: rule.service_id,
      p_rule: rule as any,
      p_input: testData as any,
    });
    if (error) {
      toast({ title: 'Estimate failed', description: error.message, variant: 'destructive' });
      return;
    }

    const estimate = {
      minPrice: Math.round((data as any).minPrice),
      maxPrice: Math.round((data as any).maxPrice),
      suggestedPrice: Math.round((data as any).suggestedPrice),
      confidence: (data as any).confidence,
      factors: ((data as any).factors || []) as string[],
    } as any;
    setCurrentEstimate(estimate);
    onTestEstimate(estimate);
    toast({ title: 'Estimate Generated', description: `Price range: ${formatCurrency(estimate.minPrice)} - ${formatCurrency(estimate.maxPrice)}` });
  };

  const handleSaveRule = (data: PriceRuleForm) => {
    onSaveRule(data);
    toast({
      title: "Rule Saved",
      description: "Pricing rule has been saved successfully",
    });
  };

  return (
    <div className="space-y-6 w-full overflow-hidden">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 break-words">
            <Brain className="h-5 w-5" />
            {t('estimator.title')}
          </CardTitle>
          <CardDescription className="break-words">
            {t('estimator.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="max-w-full overflow-x-auto">
          <Tabs defaultValue="setup" className="w-full min-w-0">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="setup">{t('estimator.tabs.setup_rules')}</TabsTrigger>
              <TabsTrigger value="test">{t('estimator.tabs.test_estimator')}</TabsTrigger>
              <TabsTrigger value="insights">{t('estimator.tabs.ai_insights')}</TabsTrigger>
            </TabsList>

            <TabsContent value="setup" className="space-y-6">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSaveRule)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="service_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Service</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a service" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {services.map((service) => (
                                <SelectItem key={service.id} value={service.id}>
                                  {service.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="base_price"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Base Price ($)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.01"
                              {...field}
                              onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Multipliers</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <FormField
                        control={form.control}
                        name="area_multiplier"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Area Multiplier</FormLabel>
                            <FormControl>
                              <Slider
                                min={0.1}
                                max={5}
                                step={0.1}
                                value={[field.value]}
                                onValueChange={(value) => field.onChange(value[0])}
                              />
                            </FormControl>
                            <div className="text-sm text-muted-foreground">
                              Current: {field.value}x
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="urgency_multiplier"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Urgency Multiplier</FormLabel>
                            <FormControl>
                              <Slider
                                min={1}
                                max={3}
                                step={0.1}
                                value={[field.value]}
                                onValueChange={(value) => field.onChange(value[0])}
                              />
                            </FormControl>
                            <div className="text-sm text-muted-foreground">
                              Current: {field.value}x
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="complexity_multiplier"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Complexity Multiplier</FormLabel>
                            <FormControl>
                              <Slider
                                min={0.5}
                                max={2}
                                step={0.1}
                                value={[field.value]}
                                onValueChange={(value) => field.onChange(value[0])}
                              />
                            </FormControl>
                            <div className="text-sm text-muted-foreground">
                              Current: {field.value}x
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="min_price"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Minimum Price ($)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.01"
                              {...field}
                              onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="max_price"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Maximum Price ($)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.01"
                              {...field}
                              onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="auto_quote_enabled"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">Auto-Quote Enabled</FormLabel>
                          <div className="text-sm text-muted-foreground">
                            Automatically generate quotes for requests within your rules
                          </div>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <Button type="submit" className="w-full">
                    Save Pricing Rule
                  </Button>
                </form>
              </Form>
            </TabsContent>

            <TabsContent value="test" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Area (m²)</Label>
                  <Input
                    type="number"
                    value={testData.area}
                    onChange={(e) => setTestData(prev => ({ ...prev, area: parseInt(e.target.value) || 0 }))}
                  />
                </div>

                <div>
                  <Label>Urgency</Label>
                  <Select value={testData.urgency} onValueChange={(value) => setTestData(prev => ({ ...prev, urgency: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Complexity</Label>
                  <Select value={testData.complexity} onValueChange={(value) => setTestData(prev => ({ ...prev, complexity: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="simple">Simple</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="complex">Complex</SelectItem>
                      <SelectItem value="very_complex">Very Complex</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Location</Label>
                  <Select value={testData.location} onValueChange={(value) => setTestData(prev => ({ ...prev, location: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rural">Rural</SelectItem>
                      <SelectItem value="suburban">Suburban</SelectItem>
                      <SelectItem value="urban">Urban</SelectItem>
                      <SelectItem value="downtown">Downtown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button onClick={handleTestEstimate} className="w-full">
                Generate Estimate
              </Button>

              {currentEstimate && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calculator className="h-5 w-5" />
                      Price Estimate
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-2xl font-bold">{formatCurrency(currentEstimate.suggestedPrice)}</span>
                        <Badge variant="secondary">
                          {currentEstimate.confidence}% confidence
                        </Badge>
                      </div>
                      
                      <div className="text-sm text-muted-foreground">
                        Range: {formatCurrency(currentEstimate.minPrice)} - {formatCurrency(currentEstimate.maxPrice)}
                        <Popover>
                          <PopoverTrigger className="ml-2 text-gray-500">
                            <Info className="h-4 w-4 inline" />
                          </PopoverTrigger>
                          <PopoverContent className="w-72 text-sm">
                            <div className="font-medium mb-1">Why this price?</div>
                            <ul className="list-disc pl-4 text-muted-foreground">
                              {currentEstimate.factors.length > 0 ? currentEstimate.factors.map((f, i) => (
                                <li key={i}>{f}</li>
                              )) : <li>Standard conditions</li>}
                            </ul>
                          </PopoverContent>
                        </Popover>
                      </div>

                      <div className="space-y-2">
                        <div className="text-sm font-medium">Set Recommended Price</div>
                        <UISlider min={currentEstimate.minPrice} max={currentEstimate.maxPrice} step={1} defaultValue={[currentEstimate.suggestedPrice]} />
                        <div className="text-xs text-muted-foreground">Drag to adjust your recommended price within the AI range.</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="insights" className="space-y-6">
              <RealTimeInsights companyId={getCurrentCompanyId()} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
} 